import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Search } from 'lucide-react';
import * as api from '../../api';
import { Btn, Input, RNLLoader, Select } from '../UI';
import Pagination from '../requisition/Pagination';
import { StatusPill } from './MMRegister';
import { NAVY, asOptions, exportXlsx, fmtQty, typeLabel } from './mmUtils';

const th = { padding: '10px 12px', fontSize: 10, fontWeight: 800, color: '#fff', background: '#0b318bff', border: '1px solid black',
             textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 };
const td = { padding: '9px 12px', fontSize: 12.5, borderBottom: '1px solid var(--border-light)', verticalAlign: 'top' };
const num = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };

// A BOQ line's mapped materials: what its quantity needs, what was requested and used for it.
function Accountability({ boqItemId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.getMaterialAccountability(boqItemId).then(setRows).catch(() => setRows([])); }, [boqItemId]);
  if (rows === null) return <div style={{ padding: 10, fontSize: 12, color: 'var(--text-secondary)' }}>Loading materials…</div>;
  if (!rows.length) return <div style={{ padding: 10, fontSize: 12, color: 'var(--text-secondary)' }}>No materials mapped to this BOQ line yet.</div>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-base)' }}>
      <thead><tr>{['Material', 'Type', 'Qty / BOQ Unit', 'Needed for BOQ', 'Needed for Installed', 'Requested', 'Used on Site', 'Variance (Used − Needed)'].map(h => (
        <th key={h} style={{ ...th, position: 'static', background: '#334155', fontSize: 9.5 }}>{h}</th>))}</tr></thead>
      <tbody>
        {rows.map(r => {
          const v = r.variance == null ? null : Number(r.variance);
          return (
            <tr key={r.material_id}>
              <td style={td}><b>{r.material_name}</b><div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{r.material_code} · {r.unit_of_measure}</div></td>
              <td style={td}>{typeLabel(r.mapping_type)}</td>
              <td style={num}>{fmtQty(r.quantity_per_unit)}</td>
              <td style={num}>{fmtQty(r.expected_for_boq)}</td>
              <td style={num}>{fmtQty(r.expected_for_installed)}</td>
              <td style={num}>{fmtQty(r.requested)}</td>
              <td style={num}>{fmtQty(r.used)}</td>
              <td style={{ ...num, fontWeight: 700, color: v == null ? undefined : v > 0 ? '#b91c1c' : '#166534' }}>
                {v == null ? 'set Qty / BOQ unit' : `${v > 0 ? '+' : ''}${fmtQty(v)}`}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// BOQ progress read from QS Hub installation records and QS valuations — no data entry here.
export default function MMSiteMeasurement({ projectId, project, lists, openMapper, toast }) {
  const [f, setF] = useState({ division: '', subsection: '', search: '' });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState({ skip: 0, limit: 50 });
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);

  const params = useCallback((extra) => ({ project_id: projectId, project_division_id: f.division, dmr_subsection_id: f.subsection, search: query, ...extra }),
    [projectId, f.division, f.subsection, query]);

  useEffect(() => {
    setRows(null);
    api.listSiteMeasurement(params({ skip: page.skip, limit: page.limit }))
      .then(r => { setRows(r || []); setTotal(r?.totalCount ?? (r || []).length); })
      .catch(e => { setRows([]); toast?.show?.(e.message || 'Could not load site measurement', 'error'); });
  }, [params, page, toast]);

  const set = (k) => (v) => { setF(p => ({ ...p, [k]: v })); setPage(p => ({ ...p, skip: 0 })); };

  const doExport = async () => {
    setBusy(true);
    try {
      const all = await api.listSiteMeasurement(params({ skip: 0, limit: 5000 }));
      exportXlsx(all || [], [
        { key: 'building', label: 'Building' }, { key: 'work_package', label: 'Work Package' }, { key: 'boq_code', label: 'BOQ Code' },
        { key: 'description', label: 'BOQ Description' }, { key: 'quantity', label: 'BOQ Qty', value: r => Number(r.quantity) },
        { key: 'unit_of_measure', label: 'UOM' }, { key: 'installed', label: 'Installed to Date', value: r => Number(r.installed) },
        { key: 'certified', label: 'Certified (QS)', value: r => Number(r.certified) }, { key: 'balance', label: 'Balance', value: r => Number(r.balance) },
        { key: 'progress_pct', label: '% Complete' }, { key: 'material_count', label: 'Mapped Materials' },
      ], `Site-Measurement-${project?.project_code || 'project'}`, 'Site Measurement');
    } catch (e) {
      toast?.show?.(e.message || 'Export failed', 'error');
    }
    setBusy(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Site Measurement (Auto-Generated)</h3>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
            Read from QS Hub daily installation records and QS valuations — no manual entry. BOQ progress is separate from material
            accountability: open a line to compare its mapped materials' needs with what was requested and used.
          </p>
        </div>
        <Btn variant="secondary" onClick={doExport} loading={busy}><Download size={14} /> Export Excel</Btn>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
        <div style={{ width: 200 }}><Select label="Building" value={f.division} onChange={set('division')} options={asOptions(lists.divisions, 'project_division_id', 'All buildings')} /></div>
        <div style={{ width: 220 }}><Select label="Work Package" value={f.subsection} onChange={set('subsection')} options={asOptions(lists.subsections, 'id', 'All work packages')} /></div>
        <div style={{ flex: '1 1 260px' }}><Input value={f.search} onChange={v => setF(p => ({ ...p, search: v }))} placeholder="Search BOQ code or description…"
                                                   onKeyDown={e => { if (e.key === 'Enter') { setQuery(f.search.trim()); setPage(p => ({ ...p, skip: 0 })); } }} /></div>
        <Btn onClick={() => { setQuery(f.search.trim()); setPage(p => ({ ...p, skip: 0 })); }}><Search size={14} /> Search</Btn>
      </div>

      {rows === null ? <RNLLoader title="Reading installation records..." /> : (
        <>
          <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                {['', 'BOQ Code', 'BOQ Description', 'Building', 'Work Package', 'BOQ Qty', 'UOM', 'Installed to Date', 'Certified (QS)', 'Balance', '% Complete', 'Mapping'].map(h => (
                  <th key={h} style={{ ...th, textAlign: ['BOQ Qty', 'Installed to Date', 'Certified (QS)', 'Balance'].includes(h) ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={12} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>No BOQ lines match.</td></tr>}
                {rows.map(r => {
                  const isOpen = !!open[r.boq_item_id];
                  return (
                    <React.Fragment key={r.boq_item_id}>
                      <tr>
                        <td style={td}>
                          <button onClick={() => setOpen(o => ({ ...o, [r.boq_item_id]: !isOpen }))} title="Material accountability"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: NAVY, padding: 0 }}>
                            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </td>
                        <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.boq_code}</td>
                        <td style={{ ...td, minWidth: 240, maxWidth: 400 }}>{r.description}</td>
                        <td style={td}>{r.building || '—'}</td>
                        <td style={td}>{r.work_package || '—'}</td>
                        <td style={num}>{fmtQty(r.quantity)}</td>
                        <td style={td}>{r.unit_of_measure}</td>
                        <td style={num}>{fmtQty(r.installed)}</td>
                        <td style={num}>{fmtQty(r.certified)}</td>
                        <td style={num}>{fmtQty(r.balance)}</td>
                        <td style={{ ...td, minWidth: 130 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--bg-base)', border: '1px solid var(--border-light)', overflow: 'hidden' }}>
                              <div style={{ width: `${r.progress_pct}%`, height: '100%', background: NAVY }} />
                            </div>
                            <b style={{ fontSize: 12 }}>{r.progress_pct}%</b>
                          </div>
                        </td>
                        <td style={td}>
                          <button onClick={() => openMapper(r.boq_item_id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} title="Open the mapping">
                            <StatusPill status={r.mapping_status} />
                          </button>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>{r.material_count} material{r.material_count === 1 ? '' : 's'}</div>
                        </td>
                      </tr>
                      {isOpen && <tr><td colSpan={12} style={{ padding: '0 0 10px 40px', borderBottom: '1px solid var(--border-light)' }}><Accountability boqItemId={r.boq_item_id} /></td></tr>}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12 }}><Pagination total={total} skip={page.skip} limit={page.limit} onChange={setPage} /></div>
        </>
      )}
    </div>
  );
}
